/**
 * Postal / zip code length rules by ISO 3166-1 alpha-2 country code.
 * Used for address form validation (min/max length per country).
 *
 * Min/max = significant character count only (digits and letters). Spaces and hyphens
 * are stripped before validation, per Universal POST*CODE® DataBase (UPU), so that
 * e.g. Canada "K1A 0B1" (7 with space) and "K1A0B1" (6) both validate as 6 significant.
 */
export interface ZipCodeRule {
    min: number;
    max: number;
}

export const ZIP_CODE_BY_COUNTRY: Record<string, ZipCodeRule> = {
    AF: {min: 4, max: 4},
    AX: {min: 5, max: 7}, // 22xxx (5) or AX-99999 (7); library has 5
    AL: {min: 4, max: 4},
    DZ: {min: 5, max: 5},
    AS: {min: 5, max: 10},
    AD: {min: 5, max: 5},
    AI: {min: 4, max: 6}, // 2640 or AI-2640; library (?:AI-)?2640
    AR: {min: 5, max: 8}, // [A-Z]9999 or +AAA; library
    AM: {min: 4, max: 6}, // (37)?9999; library
    AU: {min: 4, max: 9},
    AT: {min: 4, max: 4},
    AZ: {min: 6, max: 6},
    BH: {min: 3, max: 4},
    BD: {min: 4, max: 4},
    BB: {min: 7, max: 7},
    BY: {min: 6, max: 6},
    BE: {min: 4, max: 4},
    BM: {min: 4, max: 4},
    BT: {min: 5, max: 5},
    BA: {min: 5, max: 5},
    BR: {min: 8, max: 8}, // format 99999-999 → 8 significant
    BN: {min: 6, max: 6},
    BG: {min: 4, max: 4},
    CV: {min: 4, max: 4},
    KH: {min: 6, max: 6},
    CA: {min: 6, max: 6}, // format A9A 9A9 → 6 significant (space not counted)
    KY: {min: 7, max: 7}, // format KY9-9999 → 7 significant
    CL: {min: 7, max: 7},
    CN: {min: 6, max: 6},
    CX: {min: 4, max: 4},
    CC: {min: 4, max: 4},
    CO: {min: 6, max: 6},
    KM: {min: 8, max: 8},
    CD: {min: 7, max: 7},
    CR: {min: 5, max: 7}, // 99999 or 999-9999
    HR: {min: 5, max: 7}, // (HR-)?99999; library
    CU: {min: 5, max: 5},
    CY: {min: 6, max: 6}, // format CY-9999 → 6 significant
    CZ: {min: 5, max: 5}, // format 999 99 → 5 significant
    DK: {min: 4, max: 4},
    DJ: {min: 5, max: 5},
    DO: {min: 5, max: 5},
    EC: {min: 6, max: 6},
    EG: {min: 5, max: 7},
    SV: {min: 4, max: 4},
    EE: {min: 5, max: 5},
    SZ: {min: 4, max: 4},
    ET: {min: 4, max: 4},
    FO: {min: 5, max: 5}, // format FO-999 → 5 significant
    FM: {min: 5, max: 10},
    FI: {min: 5, max: 7}, // (FI-|AX-)?99999; library
    FR: {min: 5, max: 5},
    GF: {min: 5, max: 5},
    PF: {min: 5, max: 5},
    GA: {min: 2, max: 2},
    GE: {min: 4, max: 4},
    DE: {min: 5, max: 5},
    GH: {min: 6, max: 11},
    GI: {min: 7, max: 7}, // GX11 1AA → 7 significant (UPU format AAAA 9AA)
    GR: {min: 5, max: 5}, // format 999 99 → 5 significant
    GL: {min: 4, max: 4},
    GP: {min: 5, max: 5},
    GU: {min: 5, max: 10},
    GT: {min: 5, max: 5},
    GG: {min: 7, max: 7},
    GN: {min: 3, max: 3},
    GW: {min: 4, max: 4},
    GY: {min: 7, max: 7},
    HT: {min: 6, max: 6},
    HN: {min: 5, max: 5},
    HK: {min: 6, max: 6},
    HU: {min: 4, max: 4},
    IS: {min: 3, max: 3},
    IN: {min: 6, max: 6},
    ID: {min: 5, max: 5},
    IR: {min: 6, max: 10},
    IQ: {min: 5, max: 5},
    IE: {min: 7, max: 7},
    IM: {min: 7, max: 7},
    IL: {min: 5, max: 7}, // library \d{5,7}
    IT: {min: 5, max: 5},
    JP: {min: 7, max: 7}, // format 999-9999 → 7 significant
    JE: {min: 7, max: 7},
    JO: {min: 5, max: 5},
    KZ: {min: 6, max: 7},
    KE: {min: 5, max: 5},
    KI: {min: 6, max: 6},
    KR: {min: 5, max: 5},
    XK: {min: 5, max: 5},
    KW: {min: 5, max: 5},
    KG: {min: 6, max: 6},
    LA: {min: 5, max: 5},
    LV: {min: 4, max: 6}, // (LV-)?9999; library
    LB: {min: 4, max: 10}, // UPU PC Lgth 4 and 10
    LS: {min: 3, max: 3},
    LR: {min: 4, max: 4},
    LI: {min: 4, max: 4},
    LT: {min: 5, max: 7}, // (LT-)?99999; library
    LU: {min: 5, max: 5}, // format L-9999 → 5 significant
    MO: {min: 6, max: 6},
    MG: {min: 3, max: 3},
    MW: {min: 6, max: 6},
    MY: {min: 5, max: 5},
    MV: {min: 5, max: 5},
    MT: {min: 5, max: 7}, // AAA 99–9999; library
    MH: {min: 5, max: 10},
    MQ: {min: 5, max: 5},
    MR: {min: 5, max: 5},
    YT: {min: 5, max: 5},
    MX: {min: 5, max: 5},
    MD: {min: 6, max: 6}, // format MD-9999 → 6 significant
    MC: {min: 5, max: 5},
    MN: {min: 5, max: 10},
    ME: {min: 5, max: 5},
    MS: {min: 7, max: 7},
    MA: {min: 5, max: 5},
    MZ: {min: 4, max: 4},
    MM: {min: 5, max: 7},
    NA: {min: 5, max: 5},
    NR: {min: 5, max: 5},
    NP: {min: 5, max: 5},
    NL: {min: 4, max: 7}, // UPU PC Lgth 4 and 7 (9999 or 9999 AA)
    NC: {min: 5, max: 5},
    NZ: {min: 4, max: 4},
    NI: {min: 5, max: 5},
    NE: {min: 4, max: 4},
    NG: {min: 6, max: 6},
    NU: {min: 4, max: 4},
    NF: {min: 4, max: 4},
    MK: {min: 4, max: 4},
    MP: {min: 5, max: 10},
    NO: {min: 4, max: 4},
    OM: {min: 3, max: 3},
    PK: {min: 5, max: 5},
    PW: {min: 5, max: 10},
    PS: {min: 4, max: 8},
    PA: {min: 4, max: 4},
    PG: {min: 3, max: 3},
    PY: {min: 6, max: 6},
    PE: {min: 5, max: 5},
    PH: {min: 4, max: 4},
    PN: {min: 7, max: 7}, // PCRN 1ZZ → 7 significant (UPU format AAAA 9AA)
    PL: {min: 5, max: 5}, // format 99-999 → 5 significant
    PT: {min: 4, max: 7}, // 9999 or 9999-999; library
    PR: {min: 5, max: 10},
    RE: {min: 5, max: 5},
    RO: {min: 6, max: 6},
    RU: {min: 6, max: 6},
    BL: {min: 5, max: 5},
    SH: {min: 7, max: 7}, // STHL 1ZZ → 7 significant (UPU format AAAA 9AA)
    KN: {min: 6, max: 6},
    LC: {min: 7, max: 7}, // format LC999-999 → 7 significant
    MF: {min: 5, max: 5},
    PM: {min: 5, max: 5},
    WS: {min: 6, max: 7}, // single postcode AS 96799 = 7 significant (UPU)
    SM: {min: 5, max: 5},
    SA: {min: 5, max: 10}, // UPU PC Lgth 5, 8, 10
    SN: {min: 5, max: 5},
    RS: {min: 5, max: 6},
    SG: {min: 6, max: 6},
    SK: {min: 5, max: 5}, // format 999 99 → 5 significant
    SI: {min: 4, max: 6}, // (SI-)?9999; library
    ZA: {min: 4, max: 4},
    GS: {min: 7, max: 7}, // SIQQ 1ZZ → 7 significant (UPU format AAAA 9AA)
    ES: {min: 5, max: 5},
    LK: {min: 5, max: 5},
    VC: {min: 6, max: 6},
    SD: {min: 5, max: 5},
    SJ: {min: 4, max: 4},
    SE: {min: 5, max: 7}, // (SE-)?999 99; library
    CH: {min: 4, max: 4},
    TW: {min: 3, max: 6}, // 999 or 99999 or 999999; library
    TJ: {min: 6, max: 6},
    TZ: {min: 4, max: 5}, // library \d{4,5}
    TH: {min: 5, max: 5},
    TL: {min: 7, max: 7},
    TT: {min: 6, max: 6},
    TN: {min: 4, max: 4},
    TR: {min: 5, max: 5},
    TM: {min: 6, max: 6},
    TC: {min: 7, max: 7}, // TKCA 1ZZ → 7 significant (UPU format AAAA 9AA)
    UG: {min: 5, max: 5},
    UA: {min: 5, max: 5},
    GB: {min: 5, max: 7}, // formats A9 9AA etc. → 5–7 significant
    UY: {min: 5, max: 5},
    US: {min: 5, max: 9},
    UZ: {min: 6, max: 6},
    VA: {min: 5, max: 5},
    VE: {min: 4, max: 4},
    VN: {min: 5, max: 6}, // UPU 5; library \d{6}
    VG: {min: 6, max: 6},
    VI: {min: 5, max: 10},
    WF: {min: 5, max: 5},
    EH: {min: 5, max: 5},
    ZM: {min: 5, max: 5},
};

const DEFAULT_MIN = 3;
const DEFAULT_MAX = 12;

export function getZipCodeBounds(countryCode: string | null): {min: number; max: number} {
    if (!countryCode) {
        return {min: DEFAULT_MIN, max: DEFAULT_MAX};
    }
    const rule = ZIP_CODE_BY_COUNTRY[countryCode.toUpperCase()];
    if (!rule) {
        return {min: DEFAULT_MIN, max: DEFAULT_MAX};
    }
    return {min: rule.min, max: rule.max};
}
